/**
 * Shared @gotti/* type declaration builder.
 *
 * Both the browser sandbox (Monaco) and the backend AI tools
 * (TypeCheckTool) need to present the @gotti/* packages as typed
 * ambient modules to their respective TypeScript environments.
 *
 * This module provides:
 *   - The canonical list of which `.d.ts` files compose each package
 *   - `stripDtsNoise()` — remove `import` statements, sourceMappingURL
 *   - `buildAmbientModules()` — produce `declare module '@gotti/*'` blocks
 *
 * **Callers** are responsible for reading the raw `.d.ts` content — via
 * Vite `?raw` imports (browser) or `fs.readFileSync` (Node) — and passing
 * it in through the `GottiDtsContents` interface.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mapping of the `.d.ts` file contents that compose each @gotti package.
 *
 * File names are relative to the package `dist/` directory.
 * The caller reads these files (via `?raw` or `readFileSync`) and passes
 * the raw string content here.
 */
export interface GottiDtsContents {
  core: {
    /** `core/dist/types.d.ts` */
    types: string;
    /** `core/dist/base/System.d.ts` */
    system: string;
    /** `core/dist/Entity.d.ts` */
    entity: string;
    /** `core/dist/Component.d.ts` */
    component: string;
  };
  client: {
    /** `client/dist/ClientSystem.d.ts` */
    clientSystem: string;
    /** `client/dist/IClientSystem.d.ts` — ClientSystem, IClientSystem, ClientApiRegistry */
    iClientSystem: string;
  };
  server: {
    /** `server/dist/ServerSystem.d.ts` */
    serverSystem: string;
    /** `server/dist/IServerSystem.d.ts` — ServerSystem, IServerSystem, ServerApiRegistry */
    iServerSystem: string;
  };
  /**
   * The `@gotti/sandbox-test` package's built `.d.ts` files — the test-authoring
   * API specs import (describe/it/expect + hooks, defineSandboxTest + its
   * types, CanvasProbe). These compose the `@gotti/sandbox-test` ambient module.
   */
  sandboxTest: {
    /** `sandbox-test/dist/engine/testRuntime.d.ts` */
    testRuntime: string;
    /** `sandbox-test/dist/engine/sandboxTest.d.ts` */
    sandboxTest: string;
    /** `sandbox-test/dist/canvasProbe.d.ts` */
    canvasProbe: string;
  };
}

/**
 * Result of building ambient module declarations.
 * Each field is a full `declare module '...'` block ready to be
 * registered with Monaco's `addExtraLib` or fed into a virtual
 * CompilerHost.
 */
export interface AmbientModules {
  /** `declare module '@gotti/core' { ... }` */
  coreModule: string;
  /** `declare module '@gotti/client' { ... }` */
  clientModule: string;
  /** `declare module '@gotti/server' { ... }` */
  serverModule: string;
  /**
   * `declare module '@gotti/sandbox-test' { ... }`
   *
   * The sandbox test-authoring API specs import (describe/it/expect + hooks,
   * defineSandboxTest + its types, CanvasProbe). Built from the REAL
   * `@gotti/sandbox-test` package `.d.ts` (same as core/client/server), so the
   * types stay in sync with the implementation automatically. At runtime the
   * same module is injected by the sandbox runner / backend RunTestsTool. The
   * `ambientTestModule.spec.ts` parity test guards that the import resolves in
   * both the editor (Monaco) and backend forms.
   */
  testModule: string;
  /** `declare module '@gotti/playground' { ... }` — hand-written: a project's playground files use only its define functions. */
  playgroundModule: string;
  /** `declare module '@gotti/deployer' { ... }` — hand-written: a project's `gotti.config.ts` uses only `defineConfig`. */
  deployerModule: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants — which .d.ts files compose each package
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Relative paths (from `packages/<pkg>/dist/`) of the `.d.ts` files that
 * compose each @gotti package's ambient module declaration.
 *
 * These are the files the browser imports via `?raw` and the backend reads
 * via `fs.readFileSync`. Keeping this list here (single source of truth)
 * means adding a new export to a package automatically gets picked up by
 * both environments.
 */
export const GOTTI_DTS_PATHS = {
  core: [
    'types.d.ts',
    'base/System.d.ts',
    'Entity.d.ts',
    'Component.d.ts',
  ],
  client: [
    'ClientSystem.d.ts',
    'IClientSystem.d.ts',
  ],
  server: [
    'ServerSystem.d.ts',
    'IServerSystem.d.ts',
  ],
  // Read from `sandbox-test/dist/`. The engine files live under `engine/`
  // (mirroring `src/engine/` — vite-dts preserves the source layout).
  // `bootSandboxGame.d.ts` is intentionally excluded — it's runner
  // infrastructure, not part of the spec-authoring surface, and pulls in
  // harness types specs never touch.
  sandboxTest: [
    'engine/testRuntime.d.ts',
    'engine/sandboxTest.d.ts',
    'canvasProbe.d.ts',
  ],
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip import statements and sourceMappingURL comments from a raw
 * `.d.ts` file. These are invalid inside `declare module` blocks and
 * cause Monaco / TS errors.
 */
export function stripDtsNoise(dts: string): string {
  return dts
    .replace(/^import\s+.*?;?\s*$/gm, '')
    .replace(/\/\/# sourceMappingURL=.*$/gm, '')
    .trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The game and area file types the generated topology files are typed with, and the `Client` the web client entry
 * builds — what the editor needs so those files show no errors. Loose on purpose: the members the sandbox writes are
 * typed, everything else is allowed.
 */
const CLIENT_TOPOLOGY_DTS = `
  /** The client side of a game (\`Games/<Game>.ts\`): what \`Client.addGameFile\` takes. */
  export type ClientGameFile<TApi = {}> = {
    type: string;
    systems: unknown[];
    isNetworked: boolean;
    api?: TApi | ((data?: any, envOptions?: any) => TApi | Promise<TApi>);
    timeStep?: number;
    fixedTimeStep?: boolean;
    debug?: boolean;
    preloads?: string[];
    [hook: string]: unknown;
  };
  /** The client side of an area (\`Areas/<Area>.ts\`). */
  export type ClientAreaFile<TApi = {}> = {
    type: string;
    systems: unknown[];
    api?: TApi | ((gameData?: any, envOptions?: any) => TApi | Promise<TApi>);
    [hook: string]: unknown;
  };
  /** A game client: reaches the gate, authenticates, joins a game, and starts its area's systems. */
  export class Client {
    constructor(config: { gateServerURL: string; [option: string]: unknown }, indexedDb?: unknown, environment?: { canvas?: HTMLCanvasElement; container?: HTMLElement; [key: string]: unknown });
    addGameFile(file: ClientGameFile<any>): void;
    addAreaFile(file: ClientAreaFile<any>): void;
    authenticate(options?: unknown, token?: string): Promise<unknown>;
    joinGame(gameType: string, options?: unknown): Promise<unknown>;
  }
`;

const SERVER_TOPOLOGY_DTS = `
  /** The server side of a game (\`Games/<Game>.ts\`): what \`gotti.config.ts\` names as a game file. */
  export type ServerGameFile = {
    type: string;
    systems: unknown[];
    getInitialAreas: (...args: any[]) => string[] | Promise<string[]>;
    fixedTimeStep?: boolean;
    timeStep?: number;
    api?: (...args: any[]) => unknown;
    [hook: string]: unknown;
  };
  /** The server side of an area (\`Areas/<Area>.ts\`). */
  export type ServerAreaFile = {
    type: string;
    systems: unknown[];
    allowAllJoins?: boolean;
    serverOnly?: boolean;
    [hook: string]: unknown;
  };
`;

/** \`@gotti/playground\`'s authoring surface, as a project's \`sandbox.playground.ts\` and \`playground.config.ts\` use it. */
const PLAYGROUND_MODULE = `declare module '@gotti/playground' {
  export interface PlaygroundDefinition { title?: string; description?: string; environment?: unknown; clientConfig?: unknown; mockConfig?: unknown; [key: string]: unknown }
  export interface PlaygroundConfig { playgrounds: string | string[] | Record<string, unknown>; mockConfig?: { clientServerLatency?: number; serverServerLatency?: number }; [key: string]: unknown }
  export type PlaygroundConfigFn = (env: { command: 'serve' | 'build'; mode: string }) => PlaygroundConfig | Promise<PlaygroundConfig>;
  export function definePlayground<T extends PlaygroundDefinition>(definition: T): T;
  export function definePlaygroundConfig<T extends PlaygroundConfig | PlaygroundConfigFn>(config: T): T;
}`;

/** \`@gotti/deployer\`'s \`defineConfig\`, as a project's \`gotti.config.ts\` uses it. */
const DEPLOYER_MODULE = `declare module '@gotti/deployer' {
  export function defineConfig<T>(config: T): T;
}`;

/**
 * Build ambient module declarations (`declare module '@gotti/...'`) from
 * raw `.d.ts` file contents.
 *
 * The logic is identical to what `monacoUtils.ts` was doing inline — we
 * just moved it here so the backend can produce exactly the same type
 * declarations without duplicating the stripping / wrapping logic.
 *
 * @example
 * ```ts
 * // Browser (Vite):
 * import coreSysDts from '../../../../packages/core/dist/base/System.d.ts?raw';
 * // ... other ?raw imports ...
 * const modules = buildAmbientModules({ core: { system: coreSysDts, ... }, ... });
 * monaco.languages.typescript.typescriptDefaults.addExtraLib(modules.coreModule, ...);
 *
 * // Backend (Node):
 * const coreSysDts = fs.readFileSync(path.join(monoRoot, 'packages/core/dist/base/System.d.ts'), 'utf-8');
 * // ...
 * const modules = buildAmbientModules({ core: { system: coreSysDts, ... }, ... });
 * // Feed modules into a virtual CompilerHost
 * ```
 */
export function buildAmbientModules(dts: GottiDtsContents): AmbientModules {
  const coreContent = `
${stripDtsNoise(dts.core.types)}
${stripDtsNoise(dts.core.system)}
${stripDtsNoise(dts.core.entity)}
${stripDtsNoise(dts.core.component)}
`;

  const clientContent = `${stripDtsNoise(dts.client.clientSystem)}
${stripDtsNoise(dts.client.iClientSystem)}`;
  const serverContent = `${stripDtsNoise(dts.server.serverSystem)}
${stripDtsNoise(dts.server.iServerSystem)}`;

  const coreModule = `declare module '@gotti/core' {
${coreContent}
}`;

  // Sandbox infrastructure APIs — the runtime injects canvas, ctx, and
  // container into every client's $api via the game file. Declaring them here
  // means both Monaco and TypeCheckTool see them automatically without user
  // code needing to redeclare them. There is intentionally NO isFocused: the
  // framework routes keyboard input to the focused client automatically.
  const sandboxInfraApis = `
  // Sandbox-provided infrastructure — always available on this.$api in client systems
  export interface ClientApiRegistry {
    /** The HTML canvas element for this client */
    canvas: HTMLCanvasElement;
    /** The 2D rendering context for the canvas */
    ctx: CanvasRenderingContext2D;
    /** The DOM container element wrapping the canvas */
    container: HTMLElement;
  }
`;

  const clientModule = `declare module '@gotti/client' {
  import { System, Entity, Component, SharedApiRegistry, SystemMessage, AreaSystemRequest, ReceivedMessage, SystemParams } from '@gotti/core';
${clientContent}
${sandboxInfraApis}
${CLIENT_TOPOLOGY_DTS}
  export * from '@gotti/core';
}`;

  const serverModule = `declare module '@gotti/server' {
  import { System, Entity, Component, SharedApiRegistry, SystemMessage, ReceivedMessage, SystemParams } from '@gotti/core';
${serverContent}
${SERVER_TOPOLOGY_DTS}
  export * from '@gotti/core';
}`;

  // `@gotti/sandbox-test` ambient module — built from the package's REAL built
  // `.d.ts` (same concat + strip approach as core), so the editor/backend types
  // are always in sync with the actual implementation. No hand-authoring.
  //
  // `stripDtsNoise` strips the source's `import type { ClientApiRegistry } from
  // '@gotti/client'` / `ServerApiRegistry from '@gotti/server'` lines, so we
  // re-add them here (exactly as `clientModule` re-adds its `@gotti/core`
  // import). Those registry types are how a test's `client`/`server` `api`
  // param is auto-typed to the game's own registered api — picking up the
  // user's `declare module '@gotti/client'` augmentations.
  const testContent = `${stripDtsNoise(dts.sandboxTest.testRuntime)}
${stripDtsNoise(dts.sandboxTest.sandboxTest)}
${stripDtsNoise(dts.sandboxTest.canvasProbe)}`;
  const testModule = `declare module '@gotti/sandbox-test' {
  import { ClientApiRegistry } from '@gotti/client';
  import { ServerApiRegistry } from '@gotti/server';
${testContent}
}`;

  return { coreModule, clientModule, serverModule, testModule, playgroundModule: PLAYGROUND_MODULE, deployerModule: DEPLOYER_MODULE };
}

// ─── Monaco-specific module format ──────────────────────────────────────────
//
// buildAmbientModules() wraps each package in `declare module '...' { ... }`.
// That format is correct for the backend's virtual CompilerHost (where the
// files are NOT placed at paths that match the module name).
//
// In Monaco, extra libs are registered at file paths that TS resolves for
// module imports (e.g. file:///node_modules/@gotti/client/index.d.ts).
// TypeScript treats such files as NORMAL module files — their top-level
// exports define the module.  If the file instead contains a
// `declare module '...'` wrapper, TS treats it as an ambient declaration,
// and user `declare module` augmentations in other files do NOT merge
// because TS expects a "real" module to augment.
//
// Fix: unwrap the `declare module` to produce proper module files with
// top-level imports/exports.  User `declare module '@gotti/client'` blocks
// then merge into these modules naturally, with full concrete types.

export function unwrapDeclareModule(wrappedModule: string): string {
  // Remove `declare module '...' {\n` from start and `}` from end
  const unwrapped = wrappedModule
    .replace(/^declare module\s+'[^']+'\s*\{\n/, '')
    .replace(/\n\}\s*$/, '');
  // Remove one level of indentation (2 spaces) that was inside the block
  return unwrapped
    .replace(/^  /gm, '')
    // Strip relative re-exports (e.g. `export type { Foo } from './Bar';`)
    // that reference other files in the same package.  These are valid in
    // the original multi-file package but broken when concatenated into a
    // single extra lib because the relative paths don't resolve in
    // Monaco's virtual FS.  The actual declarations are already present
    // in the concatenated content, so removing the re-exports is safe.
    .replace(/^export\s+(?:type\s+)?\{[^}]*\}\s+from\s+['"]\.\/.*/gm, '')
    // Strip barrel re-exports (`export * from '@gotti/core'`).
    // When combined with a second interface-merge declaration for the
    // same name (e.g. the sandbox-infra ClientApiRegistry block),
    // barrel re-exports prevent `declare module` augmentations from
    // other files from flowing through interface members like $api.
    // Each @gotti package already imports what it needs explicitly, and
    // user code imports core types from '@gotti/core' directly.
    .replace(/^export \* from\s+['"]@gotti\/[^'"]+['"];?\s*$/gm, '');
}
